import axios from 'axios';
import moment from 'moment';
import cookies from 'js-cookie';
import { PORTAL_API_URL } from '../shared/constants';
import { Api } from '../shared/api';
import { select } from '../shared/utils/dom';

// use the API class to interact
export const api = new Api(PORTAL_API_URL);

/**
 * Extract an error message from applicable error responses. A generic error message is shown for 500 responses.
 * @param {*} error
 */
export function extractErrorMessageFromErrorResponse(error) {
  let message = null;
  if (!message && error && error.response && error.response.data && error.response.data.error) {
    const key = `GEN-${error.response.data.error.code}`;
    const mappedMessage = getErrorMessages()[key]; // eslint-disable-line no-use-before-define
    message = mappedMessage || { PrimaryMessage: "Error", SecondaryMessage: error.response.data.error.description };
  }
  return message || { PrimaryMessage: "Error", SecondaryMessage: "An error occurred" };
}

export function showError(messageKey, message, title = message.PrimaryMessage || 'Error') {
  const container = select('#page-alerts');
  if (!container) {
    console.log('API error can not be shown because no element exists that matches the selector #page-alerts.');
  }

  let messageType = 'error';
  if (message.Type) {
    messageType = message.Type.toLowerCase();
  }

  const html = `
      <div id="alert-${messageKey}" class="alert alert--${messageType}" id="js-online-features-unavailable" tabindex="-1" role="alert">
        <svg aria-hidden="true" class="icon icon-${messageType} " role="img">
          <use xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#icon-${messageType}"></use>
        </svg>
        <p><strong>${title}</strong>
        <br>
        ${message.SecondaryMessage || message}
        </p>
      </div>
    `;
  container.insertAdjacentHTML('afterbegin', html);
  const notification = select('#js-online-features-unavailable');
  if (notification) {
    notification.focus();
  }
}

export function showSuccessErrorMessage(successErrorMessages, messageKey) {
  if (successErrorMessages[messageKey] !== undefined) {
    const message = successErrorMessages[messageKey];

    showError(messageKey, message);
  } else {
    showError(messageKey, messageKey);
  }
}

export function removeErrors() {
  const container = select('#page-alerts');
  if (container) {
    container.innerHTML = '';
  }
}

export const getErrorMessages = () => {
  // Returned cached messages if available
  if (window.staticMessages) {
    return window.staticMessages;
  }

  const container = select("#errors");
  if (container) {
    window.staticMessages = JSON.parse(container.innerHTML);
    return window.staticMessages;
  }
  return [];
}

export const getSuccessErrorSecondaryMessage = (successErrorMessages, messageKey) => {
  if (successErrorMessages[messageKey] !== undefined) {
    return successErrorMessages[messageKey].SecondaryMessage;
  }
  return messageKey;
}

function mapAndShowErrorFromVillageErrorCode(error) {
  let key = `GEN-${error.response.data.error.code}`;
  let staticMessage = getErrorMessages()[key];
  if (!staticMessage) {
    key = "General-SystemTransactionError";
    staticMessage = getErrorMessages()[key];
  }
  if (!staticMessage) {
    staticMessage = { PrimaryMessage: "We're Sorry", SecondaryMessage: "An error has occurred" };
  }
  showError(key, staticMessage);
}

/**
 * Web agents can impersonate users. When an agent does this, the server sets a cookie with
 * the username the agent wishes to impersonate. This needs to be sent along in a special
 * header for Village requests.
 */
const getImpersonationUsername = () => {
  const impersonationUsername = null;
  if (cookies.get('vlgImpersonationUser')) {
    return cookies.get('vlgImpersonationUser');
  }
  return impersonationUsername;
}

const addImpersonationHeaderIfExists = (axiosConfig) => {
  const impersonationUsername = getImpersonationUsername();
  if (impersonationUsername) {
    axiosConfig.headers['X-NIS-IMPERSONATION-USER'] = impersonationUsername;
  }
}

const addLdcHeaderIfExists = (axiosConfig) => {
  const ldc = window.ldcCode;
  if (ldc) {
    axiosConfig.headers['X-NIS-LDC'] = ldc;
  }
}

axios.interceptors.request.use((config) => {
  removeErrors();
  addImpersonationHeaderIfExists(config); // eslint-disable-line no-use-before-define
  addLdcHeaderIfExists(config);
  return config;
}, (error) => { // eslint-disable-line arrow-body-style
  return Promise.reject(error);
});

axios.interceptors.response.use((response) => { // eslint-disable-line arrow-body-style
  return response;
}, (error) => {
  if (error.response.status === 401) {
    window.location.href = "/account/time-out";
  } else if (error.response.status === 500 || error.response.status === 403) {
    mapAndShowErrorFromVillageErrorCode(error);
  } else if (error.response.status === 504) {
    const message = { PrimaryMessage: "We're Sorry", SecondaryMessage: "Request timed out, please try again." };
    showError("gateway-error", message);
  } else {
    const message = extractErrorMessageFromErrorResponse(error);
    showError("village-error", message);
  }
  return Promise.reject(error);
});

/**
 * Get currently selected customer account ID
 */
export const getSelectedCustomerAccountId = () => {
  const selectedAccount = '0';
  if (cookies.get('selectedAccount')) {
    return cookies.get('selectedAccount');
  }
  return selectedAccount;
}

/**
 * Create account API functions go here
 */
export const createAccount = { // eslint-disable-line import/prefer-default-export

  /**
   * Lookup the secondary field for account verification
   * @param {object} data
   * @param {stirng} data.accountNumber
   * @param {string} data.phone
   * @param {bool} reproofing
   */
  getSecondaryLookupField(data, reproofing) {
    const url = `/DMEU/CustomerAccount/1.0/LookupForSecondaryValidation`;
    const params = {};
    if (data.phone) {
      params.phone = data.phone;
    } else if (data.accountNumber) {
      params.accountNumber = data.accountNumber;
    }
    params.ldc = data.ldc;
    params.reproofing = reproofing;

    return api.get(url, params, false)
      .then((response) => Promise.resolve(response));
  },

  /**
   * Call the api endpoint that looks up service addresses from account number
   * @param {object} data
   * @param {string} data.accountId AccountID being searched. Either AccountId or a pair of secondary identifiers is required unless this endpoint is being hit by an Account with the “Manage Customer Accounts” policy associated with it.
   * @param {string} data.phone Phone number associated with Customer Account. Required if AccountId is not provided
   * @param {object[]} data.secondarySearchValues[] An array of secondary lookup values (see swagger spec)
   */
  verifyAccountOwnership(data) {
    const url = (window.searchLinkedAccounts === true ? `/NIS/Identity/1.0/VerifyAccountOwnership` : `/DMEU/Identity/1.0/VerifyAccountOwnership`);
    return api.post(url, data, {}, false)
      .then((response) => Promise.resolve(response));
  },

  /**
   * Call the get ebill enrollment api endpoint to get paperless enrollment eligibility
   * @param {string} accountNumber customerAccountId to lookup.
   */
  getEbillEnrollment(accountNumber) {
    const url = `/NIS/CustomerAccount/1.0/EbillEnrollment/${accountNumber}`;
    return api.get(url, {}, false)
  },

  /**
   * Call endpoint to create the account
   */
  create(data) {
    const url = `/Identity/1.0`;
    return api.post(url, data, {}, false)
      .then((response) => Promise.resolve(response));
  },

  sendPasswordResetToken(data) {
    const url = `/NIS/Identity/1.0/RequestPasswordReset`;
    return api.post(url, data, {}, false);
  },

  sendPasswordResetTokenWebAgent(data) {
    const url = `/NIS/Identity/1.0/Agent/RequestPasswordReset`;
    return api.post(url, data, {}, false);
  },

  legacyAccountValidation(email, token) {
    const url = `/NIS/Identity/1.0/LegacyAccountValidation/${email}/${token}`;
    return api.get(url, {}, false);
  },
};

export const profileApi = {
  editProfile(editProfileRequest) {
    const url = `/NIS/Identity/1.0`;
    return api.put(url, editProfileRequest, {}, false);
  },
}

/**
 * Alert Subscription functions go here
 */
export const alertsApi = {
  addAlertSubscription(alertSubscription) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = (window.enableAlertsAndSubscriptionsForAll === true ? `/NIS/Alerts/2.0/${selectedAccount}` : `/NIS/Alerts/1.0/${selectedAccount}`);

    const postData = {
      contactMethod: alertSubscription.contactMethod,
      contactInformation: alertSubscription.contactInformation,
      time: alertSubscription.time,
      type: alertSubscription.type,
      emailValidationFailedCount: alertSubscription.emailValidationFailedCount,
    };

    return api.post(url, postData, {}, false)
      .then((response) => {
        alertSubscription.id = response.data.id;
        alertSubscription.alertType = response.data.type;
        Promise.resolve(response);
      })
  },
  deleteAlertSubscription(alertSubscription) {
    const selectedAccount = getSelectedCustomerAccountId();
    const alertId = alertSubscription.id;
    const alertType = alertSubscription.type;
    const contactMethod = alertSubscription.contactMethod;
    const url = (window.enableAlertsAndSubscriptionsForAll === true ? `/NIS/Alerts/2.0/${selectedAccount}/${alertId}/${contactMethod}` : `/NIS/Alerts/1.0/${selectedAccount}/${alertId}/${alertType}/${contactMethod}`);

    return api.delete(url, {}, false)
      .then((response) => Promise.resolve(response));
  },
  updateAlertSubscription() {
    alert('Not implemented'); // eslint-disable-line no-alert
  },
  updateIsAlertsEnrolled(enrolled, alertSubscriptionType) {
    const postData = {
      enrolled,
      alertSubscriptionType,
    };
    const url = '/alerts/updateIsAlertsEnrolled';
    return api.postToUrl(url, postData, {}, false);
  },
  updateGreenPathEnrolled(enrolled, percent) {
    const postData = {
      enrolled,
      percent,
    };
    const url = '/alerts/updateGreenPathEnrolled';
    return api.postToUrl(url, postData, {}, false);
  },
}

/**
 * Autopay API functions go here
 */
export const autopayApi = { // eslint-disable-line import/prefer-default-export
  enroll(data) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/AutoPay/1.0/${selectedAccount}`;
    const postData = {
      customerAccountId: selectedAccount,
      paymentAmount: data.amountDue,
      autopayUseBillDueDate: data.autoPayUseBillDueDate,
      updateOnetime: data.updateExistingOneTimePayment,
      updateScheduled: false,
      checkPaymentCreateRequest: {
        eftKey: data.account === 'new' ? null : data.account,
        accountNumber: data.accountNumber,
        routingNumber: data.routingNumber,
        accountType: data.accountType ? data.accountType : 'Checking',
      },
    };

    return api.post(url, postData, {}, false)
      .then((response) => Promise.resolve(response));
  },
  updateEnrollment(data) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/AutoPay/1.0/${selectedAccount}`;
    const postData = {
      customerAccountId: selectedAccount,
      paymentAmount: data.actualPaymentAmount,
      autopayUseBillDueDate: data.autopayUseBillDueDate,
      updateOnetime: data.updateExistingOneTimePayment,
      updateScheduled: data.updateScheduledAutoPay,
      checkPaymentCreateRequest: {
        eftKey: data.account === 'new' ? null : data.account,
        accountNumber: data.accountNumber,
        routingNumber: data.routingNumber,
        accountType: data.accountType ? data.accountType : 'Checking',
      },
    };

    return api.put(url, postData, {}, false)
      .then((response) => Promise.resolve(response));
  },
  cancelAutopay() {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/AutoPay/1.0/${selectedAccount}`;
    return api.delete(url, {}, false)
      .then((response) => response);
  },
  refreshEnroll() {
    const url = `/autopay/refresh`;
    return axios.get(url, {}, false);
  },
};

/**
 * One-time payment API functions go here
 */
export const oneTimePaymentApi = { // eslint-disable-line import/prefer-default-export
  makePayment(data) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/OneTimePayments/1.0/${selectedAccount}/Payment`;
    const postData = {
      customerAccountId: selectedAccount,
      paymentAmount: data.actualPaymentAmount,
      paymentDateTime: moment(data.displayDate, 'MMM D, YYYY').format("YYYY-MM-DD"),
      autopayUseBillDueDate: false,
      enrollPromo: data.onBillPromoAmountSelected,
      checkPaymentCreateRequest: {
        eftKey: data.account !== 'new' ? data.account : null,
        accountNumber: data.accountNumber,
        routingNumber: data.routingNumber,
        accountType: data.accountType,
      },
    };

    return api.post(url, postData, {}, false)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(extractErrorMessageFromErrorResponse(error)))
  },
  makeGuestPayment(customerAccountNumber, data) {
    const url = `/NIS/OneTimePayments/1.0/GuestPayment/${customerAccountNumber}`;

    return api.post(url, data, {}, false)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(extractErrorMessageFromErrorResponse(error)))
  },
  cancelPayment(confirmationNumber) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/OneTimePayments/1.0/${selectedAccount}/Payment/${confirmationNumber}`;
    return api.delete(url, null, {}, false)
      .then((response) => response)
      .catch((error) => Promise.reject(extractErrorMessageFromErrorResponse(error)))
  },
  getExistingOneTimePayment() {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/OneTimePayments/1.0/${selectedAccount}/Payment`;
    return api.get(url, {}, false)
      .then((response) => {
        if (response.status === 200) {
          return Promise.resolve(response.data);
        }

        return Promise.resolve(null);
      });
  },
  getGuestPayEligibility(customerAccountId) {
    const url = `/NIS/OneTimePayments/1.0/GuestPayment/Eligibility/${customerAccountId}`;
    return api.get(url, {}, false)
      .then((response) => {
        if (response.status === 200) {
          return Promise.resolve(response.data);
        }

        return Promise.resolve(null);
      })
      .catch((error) => Promise.reject(extractErrorMessageFromErrorResponse(error)))
  },
  getPaymentToken(customerAccountId, queryParams) {
    const url = `/NIS/OneTimePayments/1.0/PaymentToken/${customerAccountId}`;
    return api.get(url, queryParams, false)
      .then((response) => {
        if (response.status === 200) {
          return Promise.resolve(response.data);
        }

        return Promise.resolve(null);
      })
      .catch((error) => Promise.reject(extractErrorMessageFromErrorResponse(error)))
  },
};

export const achPaymentApi = { // eslint-disable-line import/prefer-default-export
  validateAccountNumber(data, isAutoPay, isOneTimePay) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/NIS/BankAccount/1.0/validate/${selectedAccount}`;
    const postData = {
      customerAccountId: selectedAccount,
      bankAccountNumber: data.accountNumber,
      routingNumber: data.routingNumber,
      autoPayEnroll: isAutoPay,
      oneTimePay: isOneTimePay,
      accountType: data.accountType,
    };
    return api.post(url, postData, {}, false)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(extractErrorMessageFromErrorResponse(error)))
  },
  sendAchRemark(data, isAutoPay, isOneTimePay) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/NIS/BankAccount/1.0/remark/${selectedAccount}`;
    const postData = {
      customerAccountNumber: selectedAccount,
      bankAccountNumber: data.accountNumber,
      routingNumber: data.routingNumber,
      autoPayEnroll: isAutoPay,
      oneTimePay: isOneTimePay,
    };
    return api.post(url, postData, {}, false)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(extractErrorMessageFromErrorResponse(error)))
  },
};

/**
 * Report payment API functions go here
 */
export const reportPayment = { // eslint-disable-line import/prefer-default-export
  reportPayment(data) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/PaymentReceipts/1.0/${selectedAccount}/Payment/Manual`;
    const postData = {
      customerAccountId: selectedAccount,
      paymentAmount: data.paymentAmount,
      paymentDateTime: moment(data.paymentDate, 'MMM D, YYYY').format("YYYY-MM-DD[T]HH:mm:ss[Z]"),
      paymentType: data.paymentType,
      paymentMethod: data.paymentMethod,
      receiptNumber: data.receiptNumber,
    };

    return api.post(url, postData, {}, false)
      .then((response) => Promise.resolve(response));
  },
};

export const deletePayment = { // eslint-disable-line import/prefer-default-export
  deleteCISPaymentMethod(data) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/NIS/BankAccount/1.0/delete/${selectedAccount}/${data.accountNumber}`;
    const postData = {
      accountNumber: selectedAccount,
      last4BankAccount: data.accountNumber,
    };

    return api.post(url, postData, {}, false)
      .then((response) => Promise.resolve(response));
  },

  deleteDISPaymentMethod(data) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/NIS/BankAccount/1.0/wallet/${selectedAccount}`;
    const postData = {
      accountNumber: data.accountNumber,
      routingNumber: data.routingNumber,
      eftKey: data.eftKey,
      actionCode: 'D',
      accountType: data.accountType === 'Checking' ? 'C' : 'S',
    };

    return api.post(url, postData, {}, false)
      .then((response) => Promise.resolve(response));
  },
};

export const addPaymentMethod = { // eslint-disable-line import/prefer-default-export
  addCISBankAccount(data) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/OneTimePayments/1.0/${selectedAccount}/Payment`;
    const postData = {
      customerAccountId: selectedAccount,
      paymentAmount: 0,
      paymentDateTime: moment(new Date()).format("YYYY-MM-DD"),
      updateOnetime: data.updateOneTimePayment,
      checkPaymentCreateRequest: {
        accountNumber: data.accountNumber,
        routingNumber: data.routingNumber,
        accountType: data.accountType,
      },
    };

    return api.post(url, postData, {}, false)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(extractErrorMessageFromErrorResponse(error)))
  },

  addDISBankAccount(data) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/NIS/BankAccount/1.0/wallet/${selectedAccount}`;
    const postData = {
      accountNumber: data.accountNumber,
      routingNumber: data.routingNumber,
      actionCode: 'A',
      accountType: data.accountType === 'Checking' ? 'C' : 'S',
      updateOnetime: data.updateOneTimePayment,
    };

    return api.post(url, postData, {}, false)
      .then((response) => Promise.resolve(response))
      .catch((error) => Promise.reject(extractErrorMessageFromErrorResponse(error)));
  },
};

export const linkedAccountsApi = {
  getLinkedAccounts() {
    const url = `/LinkedAccounts/1.0/AllSummaries`;
    return api.get(url, {}, false)
      .then((response) => Promise.resolve(response.data));
  },

  getLinkedAccountsForAgent(username) {
    const url = `/NIS/1.0/Activity/CustomerAccounts?username=${encodeURIComponent(username)}`;
    return api.get(url, {}, false)
      .then((response) => Promise.resolve(response.data));
  },

  updateLinkedAccount(customerAccountId, requestData) {
    const url = `/NIS/LinkedAccounts/1.0/${customerAccountId}`;
    return api.put(url, requestData, {}, false);
  },

  linkNewAccount(requestData) {
    const url = `/NIS/LinkedAccounts/1.0`;
    return api.post(url, requestData, {}, false);
  },

  deleteLinkedAccount(customerAccountId) {
    const url = `/NIS/LinkedAccounts/1.0/${customerAccountId}`;
    return api.delete(url, {}, false);
  },

  getMailingAddress(customerAccountId) {
    const url = `/NIS/CustomerAccount/1.0/${customerAccountId}/MailingAddress`;
    return api.get(url, {}, false);
  },

  getContactInfo(customerAccountId) {
    const url = `/NIS/CustomerAccount/1.0/${customerAccountId}/ContactInfo`;
    return api.get(url, {}, false);
  },

  updateContactInfo(customerAccountId, requestData) {
    const url = `/NIS/CustomerAccount/1.0/${customerAccountId}/ContactInfo`;
    return api.put(url, requestData, {}, false);
  },

  updateAccountProfile(customerAccountId, requestData) {
    const url = `/NIS/LinkedAccounts/1.0/${customerAccountId}/accountProfile`;
    return api.put(url, requestData, {}, false);
  },

  updateFavoriteAccount(customerAccountNumber, customerAccountStatus, isFavorite) {
    const postData = {
      customerAccountNumber,
      customerAccountStatus,
      isFavorite,
    };
    const url = '/account/updateFavoriteAccount';
    return api.postToUrl(url, postData, {}, false);
  },
};

export const budgetApi = {
  getBudgetPlanOptions() {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/Budget/1.0/${selectedAccount}/Plans`;
    return api.get(url, {}, false);
  },

  getBudgetEnrollment() {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/Budget/1.0/${selectedAccount}`;
    return api.get(url, {}, false);
  },

  enrollInBudget(budgetPlan) {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/Budget/1.0/${selectedAccount}`;
    const data = {
      paymentPlanId: budgetPlan.planId,
      paymentPlanAmount: budgetPlan.planAmount,
    };
    return api.post(url, data, {}, false);
  },

  cancelBudgetEnrollment() {
    const selectedAccount = getSelectedCustomerAccountId();
    const url = `/DMEU/Budget/1.0/${selectedAccount}`;
    return api.delete(url, {}, false);
  },
};

export const activityLogApi = {
  getLatestActivityForAccount(username, customerAccountId, from, to, limit, offset) {
    let queryString = `?username=${encodeURIComponent(username)}`;

    if (from) {
      queryString = `${queryString}&from=${from}`;
    }

    if (to) {
      queryString = `${queryString}&to=${to}`;
    }

    if (limit) {
      queryString = `${queryString}&limit=${limit}`;
    }

    if (offset) {
      queryString = `${queryString}&offset=${offset}`;
    }

    if (customerAccountId) {
      queryString = `${queryString}&customerAccountId=${customerAccountId}`;
    }
    const url = `/NIS/1.0/Activity${queryString}`;
    return api.get(url, {}, false);
  },
};

export const paperlessBillingApi = {
  enrollInPaperlessBilling(emailAddress, emailValidationFailedCount) {
    const customerAccountId = getSelectedCustomerAccountId();
    const url = `/NIS/CustomerAccount/1.0/${customerAccountId}`;
    const data = {
      ebillEnrollment: true,
      ebillEnrollmentEmail: emailAddress,
      emailValidationFailedCount,
    };
    return api.put(url, data, {}, false);
  },

  enrollInCAPaperlessBilling(customerAccountId, emailAddress) {
    const url = `/NIS/CustomerAccount/1.0/${customerAccountId}`;
    const data = {
      ebillEnrollment: true,
      ebillEnrollmentEmail: emailAddress,
    };
    return api.put(url, data, {}, false);
  },

  getPaperlessStatus() {
    const customerAccountId = getSelectedCustomerAccountId();
    const url = `/NIS/CustomerAccount/1.0/${customerAccountId}/ebill`;
    return api.get(url, {}, false);
  },
  cancelPaperlessBilling() {
    const customerAccountId = getSelectedCustomerAccountId();
    const url = `/DMEU/CustomerAccount/1.0/${customerAccountId}`;
    const data = {
      ebillEnrollment: false,
      ebillEnrollmentEmail: null,
    };
    return api.put(url, data, {}, false);
  },
};
export const portalSharedApi = {
  updateClaims() {
    const url = '/dashboard/updateclaims';
    return api.postToUrl(url, {}, {}, false);
  },
};
/**
 * Payment Plans API functions go here
 */
export const paymentPlansApi = {
  getPaymentPlans() {
    const accountId = getSelectedCustomerAccountId();
    const url = `/NIS/PaymentPlans/1.0/${accountId}`;
    return api.get(url, {}, false);
  },

  getPlanDetail(planId) {
    const accountId = getSelectedCustomerAccountId();
    const url = `/NIS/PaymentPlans/1.0/${accountId}/detail/${planId}`;
    return api.get(url, {}, false);
  },

  getPaymentPlanStatus() {
    const accountId = getSelectedCustomerAccountId();
    const url = `/NIS/PaymentPlans/1.0/${accountId}/status`;
    return api.get(url, {}, false);
  },

  enrollInPaymentPlan(enrollmentData) {
    const accountId = getSelectedCustomerAccountId();
    const url = `/NIS/PaymentPlans/1.0/${accountId}`;
    const data = {
      planId: enrollmentData.planId,
      initialPaymentAmount: enrollmentData.initialPaymentAmount ? enrollmentData.initialPaymentAmount : 0,
      initialPaymentDueDate: enrollmentData.initialPaymentDueDate ? enrollmentData.initialPaymentDueDate : null,
      termPeriodMonths: enrollmentData.termPeriodMonths ? enrollmentData.termPeriodMonths : 0,
      monthlyPayment: enrollmentData.monthlyPayment ? enrollmentData.monthlyPayment : 0,
      totalAgreementAmount: enrollmentData.totalAgreementAmount ? enrollmentData.totalAgreementAmount : 0,
    };
    return api.post(url, data, {}, false)
      .then((response) => Promise.resolve(response));
  },

  recalculatePaymentPlan(recalcData) {
    const accountId = getSelectedCustomerAccountId();
    const planId = recalcData.planId;
    const url = `/NIS/PaymentPlans/1.0/${accountId}/recalculate/${planId}`;
    const data = {
      initialPaymentAmount: recalcData.initialPaymentAmount,
    };
    return api.post(url, data, {}, false)
      .then((response) => Promise.resolve(response));
  },
};

/**
 * Usage API here
 */
export const usageApi = {
  getUsageData(utilityType) {
    const url = `/dashboard/usage?utilityType=${utilityType}`;
    return axios.get(url, {}, false);
  },
};

export const userPreferenceApi = {
  storeUserPreference(preferenceType, typeId, preferenceChoice) {
    const url = `/NIS/UserPreferences/1.0`
    const data = {
      preferenceType,
      preferenceChoice,
      typeId,
    }
    return api.post(url, data, {}, false);
  },
  getUserPreferences(preferenceType) {
    const url = `/NIS/UserPreferences/1.0?preferenceType=${preferenceType}`
    return api.get(url, {}, false);
  },
}

export const greenPathApi = {
  enrollGreenPathRider(ldc, accountNumber, gprActionCode, gprIntPercent) {
    const url = `/NIS/GPR/1.0/SetupGPR`
    const data = {
      ldc,
      accountNumber,
      gprActionCode,
      gprIntPercent,
    }
    return api.post(url, data, {}, false);
  },
}



// WEBPACK FOOTER //
// ./src/assets/toolkit/scripts/portal/api.js